import * as chrono from "chrono-node";
import { formatRule, nextDueDate, type Rule } from "./recurrence";
import { toUGX } from "./money";
import { APP_TIMEZONE, localDateKey } from "./time";

// Quick-add grammar (project-description.md §10, PL-1). Pure and synchronous
// so the preview updates on every keystroke and works offline (D37: no
// server-side parser).
//
//   Pay rent Fri 5pm #bills 30m   → task, due Fri 17:00 Kampala, tag bills, 30 min
//   -15000 lunch momo              → expense 15,000 UGX, note "lunch", MoMo wallet
//   +200k salary bank              → income 200,000 UGX into Bank
//   Gym every weekday 6am          → task repeating Mon-Fri (RRULE subset, lib/recurrence.ts)
//   anything else                  → inbox task

// Re-exported so existing imports keep working; the code lives in money.ts.
export { toUGX };

export type WalletHint = "cash" | "momo" | "airtel" | "bank";

export type ParsedTask = {
	kind: "task";
	/** Text left after removing tags, duration and date words. May be empty. */
	title: string;
	tags: string[];
	estimateMin: number | null;
	/** Day in `timeZone`, `YYYY-MM-DD`; set whenever a date or time was typed. */
	dueDate: string | null;
	/** Exact instant; only when a clock time was typed ("Fri 5pm", not "Fri"). */
	dueAt: Date | null;
	/** The words that produced the date, for the preview. */
	dateText: string | null;
	/**
	 * Canonical RRULE subset ("FREQ=DAILY", ...) or null. A repeating task always
	 * gets a dueDate: the typed one, else its first occurrence from today.
	 */
	recurrence: string | null;
};

export type ParsedMoney = {
	kind: "expense" | "income";
	/** Whole UGX, always positive; `kind` carries the direction. */
	amount: number;
	note: string;
	wallet: WalletHint | null;
	tags: string[];
	/** When the money moved, if a date was typed ("yesterday"); otherwise now. */
	occurredAt: Date | null;
	dateText: string | null;
};

export type ParsedQuickAdd = ParsedTask | ParsedMoney;
export type QuickAddKind = ParsedQuickAdd["kind"];

export type ParseOptions = { now?: Date; timeZone?: string };

const MAX_TITLE = 500;
const MAX_ESTIMATE_MIN = 1440;

export function parseQuickAdd(input: string, opts: ParseOptions = {}): ParsedQuickAdd | null {
	const text = input.trim();
	if (!text) return null;
	const now = opts.now ?? new Date();
	const timeZone = opts.timeZone ?? APP_TIMEZONE;
	return parseMoney(text, now, timeZone) ?? parseTask(text, now, timeZone);
}

// ---------------------------------------------------------------- tasks

function parseTask(text: string, now: Date, timeZone: string): ParsedTask {
	const tagged = extractTags(text);
	const repeated = extractRecurrence(tagged.rest);
	const timed = extractDuration(repeated.rest);
	const dated = extractDate(timed.rest, now, timeZone, true);

	let dueDate = dated.date ? localDateKey(dated.date, timeZone) : null;
	let recurrence: string | null = null;
	if (repeated.rule) {
		const rule = repeated.rule;
		if (!dueDate) {
			const today = localDateKey(now, timeZone);
			// Weekday rules typed at the weekend start on Monday.
			dueDate =
				rule.freq === "weekdays" && isWeekend(today)
					? nextDueDate({ ...rule, interval: 1 }, today)
					: today;
		}
		// Pin the day of month so a task on the 31st does not drift to the 28th after February.
		if (rule.freq === "monthly") rule.byMonthDay = Number(dueDate.slice(8, 10));
		recurrence = formatRule(rule);
	}

	return {
		kind: "task",
		title: cleanTitle(dated.rest).slice(0, MAX_TITLE),
		tags: tagged.tags,
		estimateMin: timed.minutes,
		dueDate,
		dueAt: dated.date && dated.hasTime ? dated.date : null,
		dateText: dated.text,
		recurrence,
	};
}

// "every day", "every 2 weeks", "every other month", "every weekday",
// "every friday" (weekly; "friday" stays for the date parser), and a trailing
// "daily" / "weekly" / "monthly" / "weekdays". Mid-sentence "daily" is left
// alone so "Read daily news" stays a plain title.
const EVERY = /(?:^|\s)every\s+(?:(other|\d{1,2})\s+)?(day|weekday|workday|week|month)s?(?=\s|$)/i;
const EVERY_WEEKDAY_NAME =
	/(?:^|\s)every(?=\s+(?:mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun)(?:day|nesday|rsday|urday)?(?:\s|$))/i;
const TRAILING = /\s(daily|weekly|monthly|weekdays)$/i;

const UNIT_FREQ: Record<string, Rule["freq"]> = {
	day: "daily",
	daily: "daily",
	weekday: "weekdays",
	workday: "weekdays",
	weekdays: "weekdays",
	week: "weekly",
	weekly: "weekly",
	month: "monthly",
	monthly: "monthly",
};

function extractRecurrence(text: string): { rule: Rule | null; rest: string } {
	const every = EVERY.exec(text);
	if (every) {
		const n =
			every[1] === undefined ? 1 : every[1].toLowerCase() === "other" ? 2 : Number(every[1]);
		if (n >= 1 && n <= 99) {
			const rule: Rule = { freq: UNIT_FREQ[every[2].toLowerCase()], interval: n };
			return { rule, rest: removeMatch(text, every.index, every[0].length) };
		}
	}
	const named = EVERY_WEEKDAY_NAME.exec(text);
	if (named) {
		return {
			rule: { freq: "weekly", interval: 1 },
			rest: removeMatch(text, named.index, named[0].length),
		};
	}
	const trailing = TRAILING.exec(text.trimEnd());
	if (trailing) {
		const rule: Rule = { freq: UNIT_FREQ[trailing[1].toLowerCase()], interval: 1 };
		return { rule, rest: text.trimEnd().slice(0, trailing.index) };
	}
	return { rule: null, rest: text };
}

function isWeekend(day: string): boolean {
	const [y, m, d] = day.split("-").map(Number);
	const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
	return weekday === 0 || weekday === 6;
}

// "30m", "1h", "1h30m", "1h 30m", "90min", "2 hrs", "45 minutes". The \b
// boundaries keep "5pm" and "10am" (clock times) out.
const DURATION =
	/(?:^|\s)(?:(\d{1,2})\s*h(?:rs?|ours?)?(?:\s*(\d{1,2})\s*m(?:ins?|inutes?)?)?|(\d{1,4})\s*m(?:ins?|inutes?)?)(?=\s|$)/i;

function extractDuration(text: string): { minutes: number | null; rest: string } {
	const m = DURATION.exec(text);
	if (!m) return { minutes: null, rest: text };
	const minutes = m[3] !== undefined ? Number(m[3]) : Number(m[1]) * 60 + Number(m[2] ?? 0);
	// Out-of-range values stay in the title rather than being silently dropped.
	if (minutes < 1 || minutes > MAX_ESTIMATE_MIN) return { minutes: null, rest: text };
	return { minutes, rest: removeMatch(text, m.index, m[0].length) };
}

// ---------------------------------------------------------------- money

// Sign, then an amount: 15000, 15,000, 15k, 1.5k, 2m. No sign means a task.
const MONEY = /^([+-])\s*(\d{1,3}(?:,\d{3})+|\d+(?:\.\d+)?)\s*([km])?(?=\s|$)(.*)$/i;

const WALLETS: Record<string, WalletHint> = {
	cash: "cash",
	momo: "momo",
	mtn: "momo",
	airtel: "airtel",
	bank: "bank",
};

function parseMoney(text: string, now: Date, timeZone: string): ParsedMoney | null {
	const m = MONEY.exec(text);
	if (!m) return null;
	const amount = toUGX(m[2], m[3]);
	if (amount === null) return null;

	const tagged = extractTags(m[4]);
	const dated = extractDate(tagged.rest, now, timeZone, false);

	let wallet: WalletHint | null = null;
	const words = dated.rest.split(/\s+/).filter((w) => {
		const hint = WALLETS[w.toLowerCase()];
		if (hint && !wallet) {
			wallet = hint;
			return false;
		}
		return true;
	});

	return {
		kind: m[1] === "-" ? "expense" : "income",
		amount,
		note: cleanTitle(words.join(" ")).slice(0, MAX_TITLE),
		wallet,
		tags: tagged.tags,
		occurredAt: dated.date,
		dateText: dated.text,
	};
}

// ---------------------------------------------------------------- shared

const TAG = /(?:^|\s)#([\p{L}\p{N}_-]+)/gu;

function extractTags(text: string): { tags: string[]; rest: string } {
	const tags: string[] = [];
	const rest = text.replace(TAG, (_, tag: string) => {
		const t = tag.toLowerCase();
		if (!tags.includes(t)) tags.push(t);
		return " ";
	});
	return { tags, rest };
}

// "1800" or "2000m", but not "31 Oct" (a day and month).
const BARE_NUMBER = /^\d+[a-z]{0,3}$/i;
const CLOCK_SUFFIX = /\d\s*[ap]\.?m\.?$/i;

type DateMatch = { date: Date | null; hasTime: boolean; text: string | null; rest: string };

function extractDate(text: string, now: Date, timeZone: string, forward: boolean): DateMatch {
	// chrono reads weekdays and clock times in the reference zone given as an
	// offset in minutes, so "Fri 5pm" means 17:00 in Kampala on any device.
	const ref = { instant: now, timezone: offsetMinutes(now, timeZone) };
	// chrono reads bare numbers as dates ("chapter 2000" → a year, "2000m" →
	// a clock time). Accept only matches that pin down a day, weekday or time
	// and are more than a number with a short suffix, unless it is "5pm"-style.
	const r = chrono
		.parse(text, ref, { forwardDate: forward })
		.filter((p) => !(BARE_NUMBER.test(p.text) && !CLOCK_SUFFIX.test(p.text)))
		.find((p) => ["day", "weekday", "hour"].some((c) => p.start.isCertain(c as "day")));
	if (!r) return { date: null, hasTime: false, text: null, rest: text };
	return {
		date: r.start.date(),
		hasTime: r.start.isCertain("hour"),
		text: r.text,
		rest: removeMatch(text, r.index, r.text.length),
	};
}

/** UTC offset of `timeZone` at `date`, in minutes (Kampala: +180). */
export function offsetMinutes(date: Date, timeZone: string): number {
	const parts = new Intl.DateTimeFormat("en-US", {
		timeZone,
		hourCycle: "h23",
		year: "numeric",
		month: "numeric",
		day: "numeric",
		hour: "numeric",
		minute: "numeric",
	}).formatToParts(date);
	const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
	const wall = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"));
	return Math.round((wall - Math.floor(date.getTime() / 60_000) * 60_000) / 60_000);
}

function removeMatch(text: string, index: number, length: number): string {
	return `${text.slice(0, index)} ${text.slice(index + length)}`;
}

// Words that only made sense next to a date we removed ("Pay rent by Fri").
const DANGLING = /\s+(?:at|on|by|due|for|before|until|from|in)$/i;

function cleanTitle(text: string): string {
	let t = text.replace(/\s+/g, " ").trim();
	while (DANGLING.test(t)) t = t.replace(DANGLING, "").trim();
	return t.replace(/[\s,;:-]+$/, "").trim();
}
